/**
 * 事件筛选器组件
 * 复用经分事件探索的事件筛选器
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'umi';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { IEventFilterProps, IEventFilterItem } from './types';
import EventFilterItem from './components/EventFilterItem';
import AddButton from '../AddButton';
import FilterLogic from '@/pages/datalab/components/UserFilter/FilterLogic';
import { LANGUAGE_EN } from '@home/_config/config';
import { TFilterLogic } from '../../types';

import './index.scss';

const EventFilter: React.FC<IEventFilterProps> = (props) => {
  const { className, title, required, value: propsValue, logic: propsLogic, eventList, optionCustomMetrics, displayConfig, onChange, onLogicChange, onConfirm } = props || {};
  const [value, setValue] = useState<IEventFilterItem[]>(propsValue || []);
  const [logic, setLogic] = useState<TFilterLogic>(propsLogic || 'and');
  const [lastAddedItemId, setLastAddedItemId] = useState<string | null>(null);
  /** 当前打开的事件筛选弹窗项 id，仅能打开一个 */
  const [openItemId, setOpenItemId] = useState<string | null>(null);
  const { formatMessage, locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;

  // 受控更新 value
  useEffect(() => {
    setValue(propsValue || []);
  }, [propsValue]);

  useEffect(() => {
    setLogic(propsLogic || 'and');
  }, [propsLogic]);

  const hoursSinceRegisterOptions = useMemo(() => [
    { value: "24", text: "24" },
    { value: "72", text: "72" },
    { value: "168", text: "168" }
  ], []);

  const handleAddEventFilter = useCallback(() => {
    const newItem: IEventFilterItem = {
      id: uuidv4(),
      eventName: '',
      eventParameter: {
        type: 'market',
        regions: [],
        markets: [],
      },
      hoursSinceRegister: '',
      hoursSinceRegisterCustom: '',
      minValue: '',
      maxValue: '',
      aggregationType: '',
      occurCountOperator: '',
      occurCountValue: [],
    };
    const newValue = [...(value || []), newItem];
    setValue(newValue);
    setLastAddedItemId(newItem.id);
    setOpenItemId(newItem.id);
    onChange?.(newValue);
    onConfirm?.(newValue);
  }, [value, onChange, onConfirm]);

  const handleEventFilterItemChange = useCallback((itemId: string, itemValue: any) => {
    const newList = (value || []).map((item) => {
      if (item.id === itemId) {
        return {
          ...item,
          ...itemValue,
        };
      }
      return item;
    });
    setValue(newList);
    onChange?.(newList);
  }, [value, onChange]);

  const handleEventFilterItemConfirm = useCallback((itemId: string, itemValue: any) => {
    const newList = (value || []).map((item) => {
      if (item.id === itemId) {
        return {
          ...item,
          ...itemValue,
        };
      }
      return item;
    });
    setValue(newList);
    onChange?.(newList);
    onConfirm?.(newList);
  }, [value, onChange, onConfirm]);

  const handleDeleteEventFilter = useCallback((itemId: string) => {
    const newList = (value || []).filter((item) => item.id !== itemId);
    setValue(newList);
    setOpenItemId(null);
    onChange?.(newList);
    onConfirm?.(newList);
  }, [value, onChange, onConfirm]);

  const handleRenameEventFilter = useCallback((itemId: string, newDisplayName: string) => {
    const newList = (value || []).map((item) => {
      if (item.id === itemId) {
        return {
          ...item,
          isRenamed: true,
          displayNameEn: newDisplayName,
          displayNameCn: newDisplayName,
        };
      }
      return item;
    });
    setValue(newList);
    onChange?.(newList);
    onConfirm?.(newList);
  }, [value, onChange, onConfirm]);

  return (
    <div className={classNames('dbl-event-filter', className)}>
      {title && <div className={classNames("dbl-event-filter-title", { "is-required": required })}>{title}</div>}
      <div className={classNames('dbl-event-filter-content-wrapper', { 'dbl-event-filter-content-wrapper-logic': value?.length > 1 })}>
        {
          value?.length > 1 && <FilterLogic
            value={logic?.toUpperCase() || 'AND'}
            isEn={isEn}
            onChange={(val) => {
              const value = val?.toLowerCase() as 'and' | 'or';
              setLogic(value);
              onLogicChange?.(value);
            }}
          />
        }
        <div className="dbl-event-filter-content">
          {(value || [])?.map((item) => (
            <EventFilterItem
              key={item?.id}
              value={item}
              eventList={eventList}
              optionCustomMetrics={optionCustomMetrics}
              hoursSinceRegisterOptions={hoursSinceRegisterOptions}
              displayConfig={displayConfig}
              defaultExpand={item?.id === lastAddedItemId}
              visible={openItemId === item?.id}
              onOpen={() => setOpenItemId((prev) => (prev !== null ? prev : (item?.id ?? null)))}
              onClose={() => setOpenItemId(null)}
              onChange={(itemValue) => handleEventFilterItemChange(item?.id, itemValue)}
              onConfirm={(itemValue) => handleEventFilterItemConfirm(item?.id, itemValue)}
              onDelete={() => handleDeleteEventFilter(item?.id)}
              onRename={(newName) => handleRenameEventFilter(item?.id, newName)}
            />
          ))}
        </div>
      </div>
      <AddButton
        className={classNames('dbl-event-filter-add-button', { 'margin-t-10': value?.length > 0 })}
        text={formatMessage({ id: 'dbl_ab_experiment_event_filter_add_button' })}
        onClick={handleAddEventFilter}
      />
    </div>
  );
};

export default EventFilter;
